import { useRouter } from 'next/router'
import React, { useCallback, useMemo } from 'react'
import { observer } from 'mobx-react'
import classNames from 'classnames'
import styles from './MessageCard.module.css'
import { MyIcon } from '~/components/ui/MyIcon'

import { timeAgoForNotification } from '~/utils'
import { useMsg } from './useMsg'

import {
  getUnreadMessageCountList_unreadCountList,
  BaseMsgType,
} from '~/graphqls/types'

export type unreadInfoItem = getUnreadMessageCountList_unreadCountList & {
  href?: string
}

type Props = {
  /** 消息总数扣减 */
  primaryMsg?: unreadInfoItem[]
  systemMsg?: unreadInfoItem[]
  /** 点击某个分类 */
  onClickMsg: (msgItem: unreadInfoItem) => void
  /** 阅读单个消息 */
  onReadMsg: (msgItem: unreadInfoItem) => void
  /** 阅读所有消息 */
  onReadAllMsg: () => void
  className?: string
}

/** 一级消息类型，其他的为系统消息子类型 */
export const primaryMsgType = [
  // 合并的：赞和收藏
  BaseMsgType.CL,
  // 被关注
  BaseMsgType.ATTENTION,
  // 被同款
  BaseMsgType.COPY,
]
/** 过滤的类型 */
export const filterMsgType = [
  // 评论
  BaseMsgType.COMMENT,
  // 单独的赞
  BaseMsgType.LIKES,
  // 单独的收藏
  BaseMsgType.COLLECT,
]
/** 消息卡片 */
export const MessageCard: React.FC<Props> = observer(
  ({
    primaryMsg: _primaryMsg,
    systemMsg: _systemMsg,
    onClickMsg,
    onReadMsg,
    onReadAllMsg,
    className,
  }) => {
    const router = useRouter()
    const { readMsgAction, handleReadAllMsg } = useMsg()

    // 已读消息
    const handleReadMsg = useCallback(
      async (msgItem: unreadInfoItem) => {
        try {
          const resp = await readMsgAction({
            variables: {
              type: msgItem?.baseMsgType,
            },
          })
          if (resp?.data?.status) {
            onReadMsg?.(msgItem)
          }
        } catch (error) {}
      },
      [readMsgAction]
    )
    /** 主消息类型列表:无值时也会展示 */
    const primaryMsg = useMemo(() => {
      return [
        {
          href: '/notification?type=cl',
          iconUrl: '/images/msg/liked--dark@3x.png',
          showName: '赞和收藏',
          count: Number(
            _primaryMsg?.filter(it => it.baseMsgType === BaseMsgType.CL)[0]
              ?.count || 0
          ),
          baseMsgType: BaseMsgType.CL,
        },

        {
          href: '/notification?type=attention',
          iconUrl: '/images/msg/followed--dark@3x.png',
          showName: '被关注',
          count: Number(
            _primaryMsg?.filter(
              it => it.baseMsgType === BaseMsgType.ATTENTION
            )[0]?.count || 0
          ),
          baseMsgType: BaseMsgType.ATTENTION,
        },
        {
          href: '/notification?type=copy',
          iconUrl: '/images/msg/copy--dark@3x.png',
          showName: '被同款',
          count: Number(
            _primaryMsg?.filter(it => it.baseMsgType === BaseMsgType.COPY)[0]
              ?.count || 0
          ),
          baseMsgType: BaseMsgType.COPY,
        },
      ]
    }, [_primaryMsg])

    /** 系统消息列表 */
    const systemMsg = useMemo(() => {
      return _systemMsg?.map(it => {
        return {
          ...it,
          showName: it?.showName || '',
          count: it?.count || 0,
          content: it?.msg?.content || '',
          // 标题为无界版图时，相当于空；
          title:
            it?.msg?.title === '无界版图' || !it?.msg?.title
              ? ''
              : it?.msg?.title,
        }
      })
    }, [_systemMsg])

    return (
      <section
        className={classNames(styles.wrapper, className, 'scrollbar-hidden')}
      >
        <div className="flex justify-between items-center mb-4">
          <span className=" text-base font-semibold text-neutral-800">
            消息中心
          </span>

          <MyIcon
            onClick={() => {
              handleReadAllMsg({
                onSuccess: () => {
                  onReadAllMsg?.()
                },
              })
            }}
            icon="yijianyidu"
            className=" text-[24px] cursor-pointer text-neutral-400 hover:text-primary"
          />
        </div>
        <div className="flex items-center justify-between">
          {!!primaryMsg?.length &&
            primaryMsg?.map(item => {
              return (
                <div
                  className="text-center cursor-pointer"
                  key={item.baseMsgType}
                  onClick={() => {
                    onReadMsg?.(item as unreadInfoItem)
                    router.push(item.href)
                  }}
                >
                  <div className="relative">
                    <img
                      className="w-12 h-12 block"
                      src={item.iconUrl}
                      alt=""
                    />
                    {item.count > 0 ? (
                      <div className="rounded-full border-solid border-2 border-neutral-100 text-[12px] leading-none absolute -top-[2px] left-[30px] bg-[#EF466F] text-neutral-800 py-0.5 px-1">
                        {item.count > 99 ? '99+' : item.count}
                      </div>
                    ) : null}
                  </div>
                  <div className="text-xs text-neutral-800 mt-2">
                    {item.showName}
                  </div>
                </div>
              )
            })}
        </div>
        <div>
          {!!systemMsg?.length &&
            systemMsg.map(item => {
              return (
                <div
                  className="flex mt-4 text-center cursor-pointer"
                  key={item.baseMsgType}
                  onClick={() => {
                    onClickMsg?.(item)
                    handleReadMsg?.(item)
                  }}
                >
                  <img
                    className="w-12 h-12 block mr-2"
                    src={item.iconUrl as string}
                    alt=""
                  />

                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <span className=" text-[14px] font-medium text-neutral-800">
                        {item.showName}
                      </span>
                      <span className=" text-xs font-normal text-neutral-400">
                        {timeAgoForNotification(item.msg?.time)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center ">
                      <span className="line-clamp-1 text-left text-xs font-normal text-neutral-400">
                        {item?.content || item?.title}
                      </span>
                      {/** 有消息时：屏蔽状态展示红点，否则展示数量 */}

                      {item.count > 0 ? (
                        item?.shield ? (
                          <span className="w-[10px] h-[10px] rounded-full bg-error ml-1 shrink-0"></span>
                        ) : (
                          <div className="rounded-full text-[12px] leading-none bg-[#EF466F] text-neutral-800 py-0.5 px-1 text-center ml-1">
                            {item.count > 99 ? '99+' : item.count}
                          </div>
                        )
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}
        </div>
      </section>
    )
  }
)
